import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography
} from '@mui/material';
import {
  checkUnitData,
  checkTourSlots,
  checkLeadSources,
  checkWorkflows,
  checkKelseyAgent,
  checkHouseAccount,
  checkExtraIntegrationSettings,
  checkLeasingAgents,
  checkCrmMapping,
  checkMessagingServiceSender,
  runEndToEndCheck,
  ingestUnitData
} from '../../../Redux/util/Apis/onboarding';
import { CheckCard, E2EReport } from './ReadinessCheckComponents';

/**
 * Determines which community IDs passed ALL readiness checks.
 * A community passes if every check that returned results reports success for it.
 */
const getPassedCommunityIds = (results, communityIds) => {
  const checks = results.filter(Boolean);
  if (checks.length === 0) return [];

  return communityIds.filter(id =>
    checks.every(result => {
      const byCommunity = result?.details?.resultsByCommunity || {};
      // If this check didn't include the community, treat as passed
      if (!(id in byCommunity)) return true;
      return byCommunity[id].success;
    })
  );
};

const ReadinessCheckDialog = ({
  open,
  onClose,
  onOnboard,
  communityIds,
  communities,
  tenantId,
  mode = 'onboard' // 'onboard' | 'check'
}) => {
  // Check results
  const [unitResult, setUnitResult] = useState(null);
  const [tourResult, setTourResult] = useState(null);
  const [sourceResult, setSourceResult] = useState(null);
  const [workflowResult, setWorkflowResult] = useState(null);
  const [agentResult, setAgentResult] = useState(null);
  const [houseAccountResult, setHouseAccountResult] = useState(null);
  const [extraIntSettingsResult, setExtraIntSettingsResult] = useState(null);
  const [leasingAgentResult, setLeasingAgentResult] = useState(null);
  const [messagingServiceResult, setMessagingServiceResult] = useState(null);
  const [crmMappingResult, setCrmMappingResult] = useState(null);

  // Loading states
  const [checksLoading, setChecksLoading] = useState(false);
  const [e2eLoading, setE2eLoading] = useState(false);
  const [ingestLoading, setIngestLoading] = useState(false);

  // E2E report
  const [e2eReport, setE2eReport] = useState(null);

  // Error state
  const [error, setError] = useState(null);

  // Track individual check loading for UI
  const [unitLoading, setUnitLoading] = useState(false);
  const [tourLoading, setTourLoading] = useState(false);
  const [sourceLoading, setSourceLoading] = useState(false);
  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [agentLoading, setAgentLoading] = useState(false);
  const [houseAccountLoading, setHouseAccountLoading] = useState(false);
  const [extraIntSettingsLoading, setExtraIntSettingsLoading] = useState(false);
  const [leasingAgentLoading, setLeasingAgentLoading] = useState(false);
  const [messagingServiceLoading, setMessagingServiceLoading] = useState(false);
  const [crmMappingLoading, setCrmMappingLoading] = useState(false);

  // Community name lookup
  const communityNames = useMemo(() => {
    const map = {};
    communities.forEach(c => {
      map[c._id] = c.name;
    });
    return map;
  }, [communities]);

  // Leasing-agent check is only shown when at least one selected community is
  // Entrata-integrated; the backend signals this via `applicable: false`.
  const leasingAgentApplicable = leasingAgentResult?.applicable !== false;
  // CRM-mapping check only applies to Knock-CRM clients, same signal.
  const crmMappingApplicable = crmMappingResult?.applicable !== false;

  const allChecksComplete =
    unitResult &&
    tourResult &&
    sourceResult &&
    workflowResult &&
    agentResult &&
    houseAccountResult &&
    extraIntSettingsResult &&
    leasingAgentResult &&
    messagingServiceResult &&
    crmMappingResult;
  const e2eComplete = !!e2eReport;
  const isRunning = checksLoading || e2eLoading;

  const passedIds = useMemo(
    () =>
      allChecksComplete
        ? getPassedCommunityIds(
            [
              unitResult,
              tourResult,
              sourceResult,
              workflowResult,
              agentResult,
              houseAccountResult,
              extraIntSettingsResult,
              leasingAgentApplicable ? leasingAgentResult : null,
              messagingServiceResult,
              crmMappingApplicable ? crmMappingResult : null
            ],
            communityIds
          )
        : [],
    [
      unitResult,
      tourResult,
      sourceResult,
      workflowResult,
      agentResult,
      houseAccountResult,
      extraIntSettingsResult,
      leasingAgentResult,
      leasingAgentApplicable,
      messagingServiceResult,
      crmMappingResult,
      crmMappingApplicable,
      communityIds,
      allChecksComplete
    ]
  );

  const failedIds = useMemo(
    () =>
      allChecksComplete
        ? communityIds.filter(id => !passedIds.includes(id))
        : [],
    [communityIds, passedIds, allChecksComplete]
  );

  const allPassed =
    allChecksComplete &&
    e2eComplete &&
    passedIds.length === communityIds.length;
  const somePassed =
    allChecksComplete &&
    e2eComplete &&
    passedIds.length > 0 &&
    failedIds.length > 0;

  // Run all checks + E2E automatically when dialog opens
  const runAllChecks = useCallback(async () => {
    if (!communityIds.length) return;

    setChecksLoading(true);
    setError(null);

    const runCheck = async (apiFn, setResult, setLoading) => {
      setLoading(true);
      try {
        const res = await apiFn(communityIds);
        setResult(res?.data);
      } catch (err) {
        setError(prev => prev || err?.message || 'A readiness check failed');
        // An errored check must still produce a result: allChecksComplete
        // ANDs every result, so leaving it null would suppress the summary
        // and "Onboard Passed Only" forever. Mark every selected community
        // failed explicitly — getPassedCommunityIds treats communities
        // absent from resultsByCommunity as passed.
        const message = err?.message || 'Check failed to run';
        const resultsByCommunity = {};
        communityIds.forEach(id => {
          resultsByCommunity[id] = { success: false, message };
        });
        setResult({
          status: 'failed',
          message,
          details: { resultsByCommunity }
        });
      } finally {
        setLoading(false);
      }
    };

    // Run all checks in parallel
    await Promise.all([
      runCheck(checkUnitData, setUnitResult, setUnitLoading),
      runCheck(checkTourSlots, setTourResult, setTourLoading),
      runCheck(checkLeadSources, setSourceResult, setSourceLoading),
      runCheck(checkWorkflows, setWorkflowResult, setWorkflowLoading),
      runCheck(checkKelseyAgent, setAgentResult, setAgentLoading),
      runCheck(
        checkHouseAccount,
        setHouseAccountResult,
        setHouseAccountLoading
      ),
      runCheck(
        ids => checkExtraIntegrationSettings(tenantId, ids),
        setExtraIntSettingsResult,
        setExtraIntSettingsLoading
      ),
      runCheck(
        ids => checkLeasingAgents(tenantId, ids),
        setLeasingAgentResult,
        setLeasingAgentLoading
      ),
      runCheck(
        ids => checkMessagingServiceSender(tenantId, ids, communities),
        setMessagingServiceResult,
        setMessagingServiceLoading
      ),
      runCheck(
        ids => checkCrmMapping(tenantId, ids),
        setCrmMappingResult,
        setCrmMappingLoading
      )
    ]);

    setChecksLoading(false);

    // Now run E2E
    setE2eLoading(true);
    try {
      const res = await runEndToEndCheck(tenantId, communityIds, {
        scheduleTours: true
      });
      setE2eReport(res?.data);
    } catch (err) {
      setError(prev => prev || err?.message || 'End-to-end check failed');
    } finally {
      setE2eLoading(false);
    }
  }, [communityIds, communities, tenantId]);

  useEffect(() => {
    if (open) {
      // Reset state
      setUnitResult(null);
      setTourResult(null);
      setSourceResult(null);
      setWorkflowResult(null);
      setAgentResult(null);
      setHouseAccountResult(null);
      setExtraIntSettingsResult(null);
      setLeasingAgentResult(null);
      setMessagingServiceResult(null);
      setCrmMappingResult(null);
      setE2eReport(null);
      setError(null);
      setIngestLoading(false);
      // Kick off checks
      runAllChecks();
    }
  }, [open]);

  const handleIngest = async () => {
    setIngestLoading(true);
    try {
      const failedUnitIds = Object.entries(
        unitResult?.details?.resultsByCommunity || {}
      )
        .filter(([, r]) => !r.success)
        .map(([id]) => id);
      if (failedUnitIds.length === 0) return;
      await ingestUnitData(tenantId, failedUnitIds);
    } catch (err) {
      setError(err?.message || 'Ingestion failed');
    } finally {
      setIngestLoading(false);
    }
  };

  const hasUnitFailures =
    unitResult &&
    Object.values(unitResult?.details?.resultsByCommunity || {}).some(
      r => !r.success
    );

  return (
    <Dialog
      open={open}
      onClose={isRunning ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '60vh' } }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Launch Readiness Checks
          </Typography>
          <Chip
            label={`${communityIds.length} communit${
              communityIds.length === 1 ? 'y' : 'ies'
            } selected`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {mode === 'check'
            ? 'Running readiness checks and end-to-end tests on selected communities.'
            : 'Running readiness checks and end-to-end tests on selected communities before staging.'}
        </Typography>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {e2eLoading && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Creating test leads, scheduling tours, and polling for AFM sync.
            This may take a minute or two. Test data will be automatically
            cleaned up.
          </Alert>
        )}

        {/* Readiness Check Results */}
        {crmMappingApplicable && (
          <CheckCard
            title="CRM Mapping"
            result={crmMappingResult}
            loading={crmMappingLoading}
            communityNames={communityNames}
          />
        )}
        <CheckCard
          title="Unit Data"
          result={unitResult}
          loading={unitLoading}
          communityNames={communityNames}
          onAction={
            hasUnitFailures && !ingestLoading
              ? { label: 'Ingest Unit Data', handler: handleIngest }
              : null
          }
        />
        <CheckCard
          title="Tour Slots"
          result={tourResult}
          loading={tourLoading}
          communityNames={communityNames}
        />
        <CheckCard
          title="Lead Sources"
          result={sourceResult}
          loading={sourceLoading}
          communityNames={communityNames}
        />
        <CheckCard
          title="Workflow Config"
          result={workflowResult}
          loading={workflowLoading}
          communityNames={communityNames}
        />
        <CheckCard
          title="Kelsey Agent"
          result={agentResult}
          loading={agentLoading}
          communityNames={communityNames}
        />
        <CheckCard
          title="House Account"
          result={houseAccountResult}
          loading={houseAccountLoading}
          communityNames={communityNames}
        />
        <CheckCard
          title="Integration Settings"
          result={extraIntSettingsResult}
          loading={extraIntSettingsLoading}
          communityNames={communityNames}
        />
        <CheckCard
          title="Messaging Service Sender"
          result={messagingServiceResult}
          loading={messagingServiceLoading}
          communityNames={communityNames}
        />
        {leasingAgentApplicable && (
          <CheckCard
            title="Entrata Leasing Agent Sync"
            result={leasingAgentResult}
            loading={leasingAgentLoading}
            communityNames={communityNames}
          />
        )}

        {/* E2E Report */}
        {e2eLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="subtitle1">
              Running End-to-End Test...
            </Typography>
          </Box>
        )}
        <E2EReport report={e2eReport} communityNames={communityNames} />

        {/* Summary after all checks complete */}
        {allChecksComplete && e2eComplete && (
          <>
            <Divider sx={{ my: 2 }} />
            {allPassed ? (
              <Alert severity="success">
                All {communityIds.length} communities passed all readiness
                checks.
              </Alert>
            ) : (
              <Alert severity="warning">
                {passedIds.length} of {communityIds.length} communities passed
                all checks.
                {failedIds.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" fontWeight={600}>
                      Failed:
                    </Typography>
                    {failedIds.map(id => (
                      <Chip
                        key={id}
                        label={communityNames[id] || id}
                        size="small"
                        color="error"
                        variant="outlined"
                        sx={{ mr: 0.5, mt: 0.5 }}
                      />
                    ))}
                  </Box>
                )}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={isRunning}>
          {mode === 'check' ? 'Close' : 'Cancel'}
        </Button>
        <Box sx={{ flex: 1 }} />
        {mode === 'onboard' && (
          <>
            {somePassed && (
              <Button
                variant="outlined"
                color="primary"
                disabled={isRunning}
                onClick={() => onOnboard(passedIds)}
              >
                Onboard Passed Only ({passedIds.length})
              </Button>
            )}
            <Button
              variant="contained"
              disabled={isRunning}
              onClick={() => onOnboard(communityIds)}
            >
              {isRunning ? (
                <>
                  <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                  Running Checks...
                </>
              ) : (
                `Onboard All (${communityIds.length})`
              )}
            </Button>
          </>
        )}
        {mode === 'check' && isRunning && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Running Checks...
            </Typography>
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReadinessCheckDialog;
